import { useState, useEffect } from "react";
import { toast } from "./Toast";
import "./EmailDetails.css";
import ResponseModal from "./ResponseModal";
import FeedbackModal from "./FeedbackModal";
import ForwardModal from "./ForwardModal";
import ReassignModal from "./ReassignModal";

import {
  generateReply,
  sendReply,
  setApprovalStatus,
  toggleArchiveStatus,
  returnForReclassification,
  forwardEmail,
  reassignEmail,
  fetchAttachmentBlob,
} from "../api";

export default function EmailDetails({ email, isSuperuser, onActionComplete }) {
  const [modalType, setModalType] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState({});

  useEffect(() => {
    if (!email?.attachments?.length) {
      setAttachmentUrls({});
      return;
    }
    const urls = {};
    const loadAttachments = async () => {
      for (const att of email.attachments) {
        if (att.path && (att.mime_type?.startsWith("image/") || att.mime_type === "application/pdf")) {
          try {
            const blob = await fetchAttachmentBlob(email.id, att.filename);
            urls[att.filename] = URL.createObjectURL(blob);
          } catch {
            // skip
          }
        }
      }
      setAttachmentUrls(urls);
    };
    loadAttachments();
    return () => Object.values(urls).forEach(URL.revokeObjectURL);
  }, [email?.id, email?.attachments]);

  if (!email) {
    return (
      <div className="no-email-selected">
        <div className="no-email-icon" aria-hidden="true">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5z" />
            <path d="M4 13h4l2 3h4l2-3h4" />
          </svg>
        </div>
        <div className="no-email-title">აირჩიეთ წერილი</div>
        <div className="no-email-hint">მარცხენა პანელიდან აირჩიეთ წერილი დეტალების სანახავად</div>
      </div>
    );
  }

  const replyStatus = email?.reply?.status;
  const isSent = replyStatus === "sent";
  const isArchived = email.is_archived === true;

  async function handleGenerateReply(choice) {
    setLoading(true);
    try {
      const data = await generateReply(email.id, choice);
      setReplyText(data.gpt_generated_reply);
      setModalType("reply");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleSendReply(finalText, action, attachPdf = false) {
    setLoading(true);
    try {
      const response = await sendReply(email.id, finalText, action, attachPdf);
      toast(response.message, "success");
      setModalType(null);
      onActionComplete();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleApproval(status) {
    if (!window.confirm(`დარწმუნებული ხართ?`)) return;
    setLoading(true);
    try {
      const response = await setApprovalStatus(email.id, status);
      toast(response.message, "success");
      onActionComplete();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleArchive() {
    setLoading(true);
    try {
      const response = await toggleArchiveStatus(email.id);
      toast(response.message, "success");
      onActionComplete(email.id);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleForward(recipient, comment) {
    setLoading(true);
    try {
      const response = await forwardEmail(email.id, recipient, comment);
      toast(response.message, "success");
      setShowForwardModal(false);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleReturnAI(comment) {
    setLoading(true);
    try {
      const response = await returnForReclassification(email.id, comment);
      toast(response.message, "success");
      setShowFeedbackModal(false);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleReassign(departmentIds) {
    setLoading(true);
    try {
      const response = await reassignEmail(email.id, departmentIds);
      toast(response.message, "success");
      setShowReassignModal(false);
      onActionComplete();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="email-details-container">
      <h2 className="email-title">{email.subject}</h2>

      <div className="meta-card">
        <p><strong>გამომგზავნი:</strong> {email.from_email}</p>
        <p><strong>დეპარტამენტი:</strong> {email.department_names?.length > 1
          ? email.department_names.join(", ")
          : email.department_name}</p>
        <p><strong>თარიღი:</strong> {email.date_str}</p>
        {replyStatus === "pending_approval" && (
          <span className="status-pill pending">მოლოდინშია</span>
        )}
      </div>

      <div className="body-card">{email.text_body}</div>

      {email.attachments?.length > 0 && (
        <div className="attachments-card">
          <div className="attachments-header">
            &#128206; თანდართული ფაილები ({email.attachments.length})
          </div>
          <div className="attachments-list">
            {email.attachments.map((att, i) => (
              <div key={i} className="attachment-item">
                {att.mime_type?.startsWith("image/") && attachmentUrls[att.filename] ? (
                  <img
                    src={attachmentUrls[att.filename]}
                    alt={att.filename}
                    className="attachment-preview"
                  />
                ) : att.mime_type === "application/pdf" && attachmentUrls[att.filename] ? (
                  <div className="attachment-pdf">
                    <iframe
                      src={attachmentUrls[att.filename]}
                      title={att.filename}
                      className="pdf-preview"
                    />
                    <div className="attachment-pdf-actions">
                      <span className="attachment-pdf-name">&#128196; {att.filename}</span>
                      <a
                        href={attachmentUrls[att.filename]}
                        target="_blank"
                        rel="noreferrer"
                        className="attachment-open-btn"
                      >
                        &#128194; გახსნა
                      </a>
                      <a
                        href={attachmentUrls[att.filename]}
                        download={att.filename}
                        className="attachment-download-btn"
                      >
                        &#11015; ჩამოტვირთვა
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="attachment-file">
                    &#128196; {att.filename}
                    <span className="attachment-size">
                      ({(att.size / 1024).toFixed(1)} KB)
                    </span>
                    {attachmentUrls[att.filename] && (
                      <a
                        href={attachmentUrls[att.filename]}
                        download={att.filename}
                        className="attachment-download-btn"
                      >
                        &#11015; ჩამოტვირთვა
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Show rejection comment if reply was sent back */}
      {replyStatus === "draft" && email.reply?.rejection_comment && (
        <div className="rejection-notice">
          <strong>ადმინის კომენტარი:</strong> {email.reply.rejection_comment}
        </div>
      )}

      {isArchived ? (
        <>
          <h3 className="section-title">არქივი</h3>
          <button className="action-btn neutral" onClick={handleToggleArchive} disabled={loading}>
            არქივიდან აღდგენა
          </button>
        </>
      ) : isSent ? (
        <>
          <h3 className="section-title">გაგზავნილია</h3>
          <button className="action-btn neutral" onClick={handleToggleArchive} disabled={loading}>
            არქივში გადატანა
          </button>
        </>
      ) : (
        <>
          {/* Approval actions — only when reply is pending approval and user is admin */}
          {isSuperuser && replyStatus === "pending_approval" && (
            <>
              <h3 className="section-title">დამტკიცება</h3>
              <div className="actions-grid">
                <button className="action-btn success" onClick={() => handleApproval("approved")} disabled={loading}>
                  დამტკიცება და გაგზავნა
                </button>
                <button className="action-btn danger" onClick={() => handleApproval("draft")} disabled={loading}>
                  უარყოფა
                </button>
              </div>
            </>
          )}

          <div className="actions-section">
            <h3 className="section-title">პასუხი</h3>
            <div className="actions-group">
              <button className="action-btn success" onClick={() => handleGenerateReply("Yes")} disabled={loading}>
                <span className="action-icon">&#10003;</span> დადებითი პასუხი
              </button>
              <button className="action-btn danger" onClick={() => handleGenerateReply("No")} disabled={loading}>
                <span className="action-icon">&#10007;</span> უარყოფითი პასუხი
              </button>
              <button className="action-btn info" onClick={() => setModalType("custom")}>
                <span className="action-icon">&#9997;</span> ხელით წერა
              </button>
            </div>
          </div>

          <div className="actions-section">
            <h3 className="section-title">სხვა მოქმედებები</h3>
            <div className="actions-group secondary">
              <button className="action-btn-sm" onClick={() => setShowForwardModal(true)}>
                &#10148; გადაგზავნა
              </button>
              <button className="action-btn-sm" onClick={() => setShowReassignModal(true)}>
                &#8644; დეპარტამენტის შეცვლა
              </button>
              <button className="action-btn-sm" onClick={() => setShowFeedbackModal(true)}>
                &#8635; AI-სთვის დაბრუნება
              </button>
              <button className="action-btn-sm" onClick={handleToggleArchive}>
                &#128451; დაარქივება
              </button>
            </div>
          </div>
        </>
      )}

      {modalType && (
        <ResponseModal
          emailId={email.id}
          type={modalType}
          defaultText={replyText}
          onClose={() => setModalType(null)}
          onSend={handleSendReply}
          isSuperuser={isSuperuser}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleReturnAI}
        />
      )}

      {showForwardModal && (
        <ForwardModal
          onClose={() => setShowForwardModal(false)}
          onSubmit={handleForward}
        />
      )}

      {showReassignModal && (
        <ReassignModal
          currentDepartment={email.department_name}
          onClose={() => setShowReassignModal(false)}
          onSubmit={handleReassign}
        />
      )}
    </div>
  );
}
